import { Component } from '@theme/component';

class TabContent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  #init() {
    // Shopify's Blocks API wraps each block in a <div>. Find direct children
    // (the wrappers) that contain a [data-tab-title] descendant.
    /** @type {{ wrapper: HTMLElement, title: string, url: string }[]} */
    const wrapperPanels = [];
    for (const wrapper of this.children) {
      const panel = wrapper.matches('[data-tab-title]')
        ? wrapper
        : wrapper.querySelector('[data-tab-title]');
      if (panel instanceof HTMLElement && panel.dataset.tabTitle) {
        wrapperPanels.push({
          wrapper: /** @type {HTMLElement} */ (wrapper),
          title: panel.dataset.tabTitle,
          url: panel.dataset.collectionUrl ?? '',
        });
      }
    }

    /** @type {HTMLAnchorElement | null} */
    const generalButton = this.querySelector('[data-tab-general-button] a');
    const urls = wrapperPanels.map((d) => d.url);
    const titles = wrapperPanels.map((d) => d.title);

    const panelWrappers = wrapperPanels.map((d) => d.wrapper);

    // Static heading block (a Text block rendered by the template). Only treat
    // it as present when it actually has content, so an empty heading doesn't
    // add stray spacing. It gets grouped with the tab nav into `.cc-tab-header`
    // so the "stacked" / "inline" layout setting can arrange the two.
    const headingEl = this.querySelector(':scope > [data-tab-heading]');
    const hasHeading = headingEl instanceof HTMLElement && headingEl.textContent?.trim();
    if (headingEl && !hasHeading) headingEl.remove();

    if (generalButton && wrapperPanels[0])
      this.#updateGeneralButton(generalButton, wrapperPanels[0].url, wrapperPanels[0].title);

    if (wrapperPanels.length < 2) return;

    const nav = document.createElement('div');
    nav.className = 'cc-tab-nav';
    nav.setAttribute('role', 'tablist');

    const wrappers = panelWrappers;

    wrapperPanels.forEach(({ wrapper, title }, i) => {
      wrapper.setAttribute('role', 'tabpanel');
      if (i !== 0) wrapper.classList.add('hidden');

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cc-tab-btn';
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      btn.textContent = title;
      btn.addEventListener('click', () => this.#activate(wrappers, i, urls, titles, generalButton));
      nav.append(btn);
    });

    this.prepend(nav);
    this.#enableDragScroll(nav);

    // When placement is "inline", pull the general button ("View all") up onto
    // the tab-nav row so it aligns with the tabs. "below_tabs" is handled after
    // the header is built; "below" leaves it where the template rendered it
    // (below the tab content).
    const generalButtonWrapper = this.querySelector('[data-tab-general-button]');
    const placement = this.dataset.generalButtonPlacement ?? 'inline';
    if (generalButtonWrapper && placement === 'inline') {
      const navRow = document.createElement('div');
      navRow.className = 'cc-tab-nav-row';
      nav.replaceWith(navRow);
      navRow.append(nav, generalButtonWrapper);
    }

    // Group the heading block with the tab bar so `data-heading-layout`
    // ("stacked" / "inline") on the host element can lay them out.
    /** @type {HTMLElement} */
    let tabBar = this.querySelector('.cc-tab-nav-row') ?? nav;
    if (hasHeading && headingEl) {
      const header = document.createElement('div');
      header.className = 'cc-tab-header';
      tabBar.replaceWith(header);
      header.append(headingEl, tabBar);
      tabBar = header;
    }

    // "below_tabs": sit the general button directly under the tab bar (and
    // heading), above the tab panels.
    if (generalButtonWrapper && placement === 'below_tabs') tabBar.after(generalButtonWrapper);
  }

  /**
   * Enables click-and-drag horizontal scrolling on desktop (mouse only - touch
   * devices already get native drag-scroll from `overflow-x`/`touch-action`
   * in the CSS, and trackpad/touch pointers shouldn't fight that here).
   * @param {HTMLElement} nav
   */
  #enableDragScroll(nav) {
    let dragging = false;
    let moved = false;
    let startX = 0;
    let startScrollLeft = 0;

    nav.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || nav.scrollWidth <= nav.clientWidth) return;
      dragging = true;
      moved = false;
      startX = event.clientX;
      startScrollLeft = nav.scrollLeft;
      nav.classList.add('is-dragging');
    });

    nav.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const delta = event.clientX - startX;
      if (Math.abs(delta) > 3) moved = true;
      nav.scrollLeft = startScrollLeft - delta;
    });

    const stopDragging = () => {
      dragging = false;
      nav.classList.remove('is-dragging');
    };
    nav.addEventListener('pointerup', stopDragging);
    nav.addEventListener('pointerleave', stopDragging);
    nav.addEventListener('pointercancel', stopDragging);

    // Swallow the click a drag release would otherwise fire on a tab button.
    nav.addEventListener(
      'click',
      (event) => {
        if (!moved) return;
        moved = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  }

  /**
   * @param {Element[]} panels
   * @param {number} activeIndex
   * @param {string[]} urls
   * @param {string[]} titles
   * @param {HTMLAnchorElement | null} generalButton
   */
  #activate(panels, activeIndex, urls, titles, generalButton) {
    const btns = [...this.querySelectorAll('.cc-tab-btn')];
    panels.forEach((panel, i) => {
      const isActive = i === activeIndex;
      btns[i]?.setAttribute('aria-selected', isActive ? 'true' : 'false');
      panel.classList.toggle('hidden', !isActive);
    });
    if (generalButton) this.#updateGeneralButton(generalButton, urls[activeIndex], titles[activeIndex]);
  }

  /**
   * @param {HTMLAnchorElement} anchor
   * @param {string} [url]
   * @param {string} [title]
   */
  #updateGeneralButton(anchor, url, title) {
    if (!url) {
      anchor.removeAttribute('href');
      anchor.setAttribute('role', 'link');
      anchor.setAttribute('aria-disabled', 'true');
      return;
    }
    anchor.href = url;
    anchor.removeAttribute('role');
    anchor.removeAttribute('aria-disabled');

    const labelEl = anchor.querySelector('[data-tab-general-button-label]');
    if (!labelEl) return;
    const baseLabel = anchor.dataset.baseLabel ?? '';
    const appendTitle = anchor.dataset.appendTabTitle === 'true';
    labelEl.textContent = appendTitle && title ? `${baseLabel} ${title}`.trim() : baseLabel;
  }
}

if (!customElements.get('tab-content')) {
  customElements.define('tab-content', TabContent);
}
